/**
 * The speaker phone.
 *
 * Only the host device receives `narrate` frames, so only the host speaks -
 * mirroring the physical setup where one phone in the middle of the table
 * narrates and everyone else just listens with their eyes shut.
 *
 * Nothing is synthesised here. Every line of the script is a recording, one
 * set per voice, served from `/voices/<voice>/<clip>.mp3`; this module only
 * queues them. The browser's own speech engine did this job first, and gave a
 * different voice on every phone, most of them robotic. `docs/voices.md` says
 * how the recordings are made.
 *
 * Three browser quirks shape this module:
 *
 * 1. Phones refuse to play sound until the page has had a user gesture, and
 *    iOS grants that to one element rather than to the page. There is
 *    therefore a single `<audio>` element for the whole session: `unlock()`
 *    plays a moment of silence on it from inside a tap, and every line
 *    afterwards reuses it.
 *
 * 2. It is an `<audio>` element rather than Web Audio on purpose: an iPhone
 *    with its ringer switch off mutes the latter, and a silent narrator is
 *    the one failure nobody at the table can see.
 *
 * 3. Clips are fetched whole and played from memory, the usual ones before
 *    the round starts. A line then begins the instant it is called, even on a
 *    connection that hiccups halfway through the night.
 */

import { DEFAULT_VOICE, NARRATION_STAPLES, isVoice, narrationClips } from "./i18n/fr.js";

/**
 * What the lobby's voice test plays: the line that opens every night, and
 * long enough to judge a voice and set the volume by.
 */
const SAMPLE_CLIP = "phase.night";

/** Ten milliseconds of silence, as a WAV. Enough to arm the element. */
const SILENCE =
  "data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICA" +
  "gICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==";

export class Narrator {
  /** False until a snapshot says this device is the one that narrates. */
  private enabled = false;
  private voice = DEFAULT_VOICE;
  private player: HTMLAudioElement | null = null;
  private unlocked = false;
  /** Clip URLs waiting behind the one being played, oldest first. */
  private queue: string[] = [];
  private busy = false;
  /** Numbers the clip in flight, so a late callback from an older one is ignored. */
  private serial = 0;
  /** Clips already fetched, or on their way: URL to in-memory object URL. */
  private readonly loaded = new Map<string, Promise<string>>();

  get supported(): boolean {
    return typeof Audio !== "undefined";
  }

  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    if (enabled) void this.warm();
    else this.cancel();
  }

  /** Changes narrator, by voice id. Whatever is being said is left to finish. */
  setVoice(id: string): void {
    if (!isVoice(id) || id === this.voice) return;
    this.voice = id;
    if (this.enabled) void this.warm();
  }

  /**
   * Arms the audio element. Only works from inside a user gesture handler, and
   * is harmless anywhere else: it does nothing once armed, or while speaking.
   */
  unlock(): void {
    if (this.unlocked || this.busy || !this.supported) return;
    this.unlocked = true;
    const player = this.element();
    player.src = SILENCE;
    // A refusal is not final: the next tap simply tries again.
    player.play().catch(() => {
      this.unlocked = false;
    });
  }

  /**
   * Speaks a narration cue. Unknown keys play nothing rather than throw, so a
   * server that learns a new phase before the client does simply stays quiet.
   */
  speak(key: string, params: Record<string, string | number> = {}): void {
    if (!this.enabled || !this.supported) return;
    for (const clip of narrationClips(key, params)) this.enqueue(this.url(clip));
  }

  /** Plays the sample line straight away, for the lobby's voice test. */
  sample(): void {
    if (!this.enabled || !this.supported) return;
    this.cancel();
    this.enqueue(this.url(SAMPLE_CLIP));
  }

  cancel(): void {
    this.queue = [];
    this.serial += 1;
    this.busy = false;
    this.player?.pause();
  }

  private url(clip: string): string {
    return `/voices/${this.voice}/${clip}.mp3`;
  }

  private element(): HTMLAudioElement {
    if (!this.player) {
      this.player = new Audio();
      this.player.preload = "auto";
      // A clip that is missing or will not decode is skipped like one that
      // has finished: the night must go on a line short rather than stall.
      this.player.addEventListener("ended", () => this.finish(this.serial));
      this.player.addEventListener("error", () => this.finish(this.serial));
    }
    return this.player;
  }

  /**
   * Lines queue rather than interrupt each other: "close your eyes" for one
   * role and "wake up" for the next arrive back to back and must be heard in
   * order. Fetching starts here, so the second is ready when the first ends.
   */
  private enqueue(url: string): void {
    this.load(url).catch(() => {});
    this.queue.push(url);
    if (!this.busy) this.next();
  }

  private next(): void {
    const url = this.queue.shift();
    if (!url) return;

    this.busy = true;
    const serial = ++this.serial;
    const player = this.element();
    this.load(url)
      .then((source) => {
        if (serial !== this.serial) return undefined; // Cancelled while it loaded.
        player.src = source;
        return player.play().then(() => {
          this.unlocked = true;
        });
      })
      .catch(() => this.finish(serial));
  }

  private finish(serial: number): void {
    if (serial !== this.serial || !this.busy) return;
    this.busy = false;
    this.next();
  }

  private load(url: string): Promise<string> {
    let ready = this.loaded.get(url);
    if (!ready) {
      ready = fetch(url).then(async (response) => {
        const clip = await response.blob();
        // The server answers any unknown path with the app shell, so a
        // missing clip arrives as a perfectly successful page of HTML.
        if (!response.ok || !clip.type.startsWith("audio/")) throw new Error(`No clip at ${url}`);
        return URL.createObjectURL(clip);
      });
      // Forget a failure, so the next call for this line tries again.
      ready.catch(() => this.loaded.delete(url));
      this.loaded.set(url, ready);
    }
    return ready;
  }

  /**
   * Fetches this voice's usual lines ahead of the round, one at a time so it
   * never competes with the game for the connection. The lines that state a
   * duration are left to be fetched when called: there are dozens of them,
   * and a round only ever uses one of each.
   */
  private async warm(): Promise<void> {
    if (!this.supported) return;
    const voice = this.voice;
    for (const clip of NARRATION_STAPLES) {
      // Stop quietly once the host has moved on to another voice.
      if (voice !== this.voice || !this.enabled) return;
      await this.load(this.url(clip)).catch(() => {});
    }
  }
}
